import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, XIcon, CheckIcon, PlusIcon, LayersIcon, PackageIcon, ChevronDownIcon } from 'lucide-react';
import { ProductCard } from '../ProductCard';
import { SKUCard } from '../SKUCard';
import { SelectedItemsList } from '../SelectedItemsList';
// Mock data for products
const mockProducts = [{
  id: 'iphone-15',
  name: 'iPhone 15',
  brand: 'Apple',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/adSmqXZtwMRaADCVDQGH75/image.png',
  variants: [{
    id: 'iphone-15-128-black',
    name: 'iPhone 15 128GB Black',
    sku: 'IPH15-128-BLK',
    price: 799,
    stock: 45
  }, {
    id: 'iphone-15-128-blue',
    name: 'iPhone 15 128GB Blue',
    sku: 'IPH15-128-BLU',
    price: 799,
    stock: 32
  }, {
    id: 'iphone-15-256-black',
    name: 'iPhone 15 256GB Black',
    sku: 'IPH15-256-BLK',
    price: 899,
    stock: 18
  }, {
    id: 'iphone-15-256-blue',
    name: 'iPhone 15 256GB Blue',
    sku: 'IPH15-256-BLU',
    price: 899,
    stock: 24
  }],
  category: 'Smartphones'
}, {
  id: 'samsung-s24',
  name: 'Samsung Galaxy S24',
  brand: 'Samsung',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9CLQER3mW4v2Jv4RPGHc1W/image.png',
  variants: [{
    id: 'samsung-s24-128-black',
    name: 'Samsung Galaxy S24 128GB Black',
    sku: 'SAM-S24-128-BLK',
    price: 799,
    stock: 38
  }, {
    id: 'samsung-s24-128-white',
    name: 'Samsung Galaxy S24 128GB White',
    sku: 'SAM-S24-128-WHT',
    price: 799,
    stock: 27
  }, {
    id: 'samsung-s24-256-black',
    name: 'Samsung Galaxy S24 256GB Black',
    sku: 'SAM-S24-256-BLK',
    price: 899,
    stock: 15
  }],
  category: 'Smartphones'
}, {
  id: 'macbook-air',
  name: 'MacBook Air M2',
  brand: 'Apple',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qcMnDWqru55oVRbBxJg3SM/image.png',
  variants: [{
    id: 'macbook-air-256-silver',
    name: 'MacBook Air M2 256GB Silver',
    sku: 'MBA-M2-256-SLV',
    price: 999,
    stock: 12
  }, {
    id: 'macbook-air-256-gray',
    name: 'MacBook Air M2 256GB Space Gray',
    sku: 'MBA-M2-256-GRY',
    price: 999,
    stock: 8
  }, {
    id: 'macbook-air-512-silver',
    name: 'MacBook Air M2 512GB Silver',
    sku: 'MBA-M2-512-SLV',
    price: 1199,
    stock: 5
  }],
  category: 'Laptops'
}, {
  id: 'airpods-pro',
  name: 'AirPods Pro',
  brand: 'Apple',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/j1haWtLZkePnbgPEWS6oLi/image.png',
  variants: [{
    id: 'airpods-pro-2',
    name: 'AirPods Pro 2nd Gen',
    sku: 'APP-2',
    price: 249,
    stock: 50
  }],
  category: 'Audio'
}, {
  id: 'galaxy-tab',
  name: 'Galaxy Tab S9',
  brand: 'Samsung',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xcWkHhZn7peZ2jG4h2eosT/image.png',
  variants: [{
    id: 'galaxy-tab-s9-128',
    name: 'Galaxy Tab S9 128GB',
    sku: 'TAB-S9-128',
    price: 799,
    stock: 22
  }, {
    id: 'galaxy-tab-s9-256',
    name: 'Galaxy Tab S9 256GB',
    sku: 'TAB-S9-256',
    price: 899,
    stock: 14
  }],
  category: 'Tablets'
}];
// Create a flattened array of all SKUs
const allSKUs = mockProducts.flatMap(product => product.variants.map(variant => ({
  ...variant,
  productName: product.name,
  brand: product.brand,
  image: product.image,
  category: product.category
})));
interface ProductSelectionStepProps {
  collectionType: string;
  selectedProducts: any[];
  setSelectedProducts: (products: any[]) => void;
  selectedSKUs: any[];
  setSelectedSKUs: (skus: any[]) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}
export const ProductSelectionStep: React.FC<ProductSelectionStepProps> = ({
  collectionType,
  selectedProducts,
  setSelectedProducts,
  selectedSKUs,
  setSelectedSKUs,
  onNext,
  onBack,
  onSkip
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState(collectionType === 'sku' ? 'skus' : 'products');
  const [filteredProducts, setFilteredProducts] = useState(mockProducts);
  const [filteredSKUs, setFilteredSKUs] = useState(allSKUs);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  // Get unique categories and brands for filters
  const categories = ['all', ...Array.from(new Set(mockProducts.map(p => p.category)))];
  const brands = Array.from(new Set(mockProducts.map(p => p.brand)));
  useEffect(() => {
    // Filter products based on search query, category and brand
    let filtered = mockProducts;
    if (searchQuery) {
      filtered = filtered.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.brand.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }
    setFilteredProducts(filtered);
    // Filter SKUs
    let filteredSkus = allSKUs;
    if (searchQuery) {
      filteredSkus = filteredSkus.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.sku.toLowerCase().includes(searchQuery.toLowerCase()) || s.productName.toLowerCase().includes(searchQuery.toLowerCase()) || s.brand.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    if (selectedCategory !== 'all') {
      filteredSkus = filteredSkus.filter(s => s.category === selectedCategory);
    }
    if (selectedBrands.length > 0) {
      filteredSkus = filteredSkus.filter(s => selectedBrands.includes(s.brand));
    }
    setFilteredSKUs(filteredSkus);
  }, [searchQuery, selectedCategory, selectedBrands]);
  const toggleProductSelection = product => {
    if (selectedProducts.some(p => p.id === product.id)) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
  };
  const toggleSKUSelection = sku => {
    if (selectedSKUs.some(s => s.id === sku.id)) {
      setSelectedSKUs(selectedSKUs.filter(s => s.id !== sku.id));
    } else {
      setSelectedSKUs([...selectedSKUs, sku]);
    }
  };
  const toggleBrandFilter = brand => {
    if (selectedBrands.includes(brand)) {
      setSelectedBrands(selectedBrands.filter(b => b !== brand));
    } else {
      setSelectedBrands([...selectedBrands, brand]);
    }
  };
  const handleClearAll = () => {
    setSelectedProducts([]);
    setSelectedSKUs([]);
  };
  const totalSelectedItems = () => {
    const productCount = selectedProducts.length;
    const skuCount = selectedSKUs.length;
    const totalVariants = selectedProducts.reduce((acc, product) => acc + product.variants.length, 0);
    return {
      productCount,
      skuCount,
      totalVariants: totalVariants + skuCount
    };
  };
  const {
    productCount,
    skuCount,
    totalVariants
  } = totalSelectedItems();
  const hasSelectedItems = productCount > 0 || skuCount > 0;
  return <div className="flex flex-col h-full" data-id="element-273">
      <div className="flex flex-1 overflow-hidden" data-id="element-274">
        {/* Left side - Search & Browse */}
        <div className="w-3/5 border-r border-gray-200 p-6 overflow-y-auto" data-id="element-275">
          <div className="mb-4" data-id="element-276">
            <div className="flex justify-between items-center mb-4" data-id="element-277">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-278">
                Add Products to Collection
              </h3>
              {collectionType === 'mixed' && <div className="flex border border-gray-200 rounded-md overflow-hidden" data-id="element-279">
                  <button className={`px-3 py-1.5 text-sm ${searchMode === 'products' ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700'}`} onClick={() => setSearchMode('products')} data-id="element-280">
                    Search Products
                  </button>
                  <button className={`px-3 py-1.5 text-sm ${searchMode === 'skus' ? 'bg-green-100 text-green-700' : 'bg-white text-gray-700'}`} onClick={() => setSearchMode('skus')} data-id="element-281">
                    Search Variants
                  </button>
                </div>}
            </div>
            <div className="flex gap-4 mb-4" data-id="element-282">
              <div className="relative flex-1" data-id="element-283">
                <input type="text" placeholder={`Search ${searchMode === 'products' ? 'products' : 'variants'}...`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-284" />
                <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-285" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600" data-id="element-286">
                    <XIcon className="h-5 w-5" data-id="element-287" />
                  </button>}
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center px-4 py-2 border ${showFilters ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-300 text-gray-700'} rounded-md`} data-id="element-288">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-289" />
                Filters
              </button>
            </div>
            {/* Quick filter chips */}
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-290">
              {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-3 py-1 text-sm rounded-full ${selectedCategory === category ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} data-id="element-291">
                  {category === 'all' ? 'All Categories' : category}
                </button>)}
            </div>
            {/* Expanded filters */}
            {showFilters && <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-292">
                <h4 className="font-medium text-gray-700 mb-3" data-id="element-293">Brand</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-294">
                  {brands.map(brand => <label key={brand} className="flex items-center" data-id="element-295">
                      <input type="checkbox" checked={selectedBrands.includes(brand)} onChange={() => toggleBrandFilter(brand)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-296" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-297">
                        {brand}
                      </span>
                    </label>)}
                </div>
                <div className="mt-4" data-id="element-298">
                  <h4 className="font-medium text-gray-700 mb-2" data-id="element-299">
                    Price Range
                  </h4>
                  <div className="flex items-center gap-2" data-id="element-300">
                    <input type="number" placeholder="Min" className="w-24 p-2 border border-gray-300 rounded-md" data-id="element-301" />
                    <span data-id="element-302">-</span>
                    <input type="number" placeholder="Max" className="w-24 p-2 border border-gray-300 rounded-md" data-id="element-303" />
                  </div>
                </div>
                <div className="mt-4 flex justify-end" data-id="element-304">
                  <button type="button" onClick={() => {
                setSelectedCategory('all');
                setSelectedBrands([]);
              }} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-305">
                    Clear Filters
                  </button>
                </div>
              </div>}
          </div>
          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-306">
            {searchMode === 'products' ? filteredProducts.length > 0 ? filteredProducts.map(product => <ProductCard key={product.id} product={product} isSelected={selectedProducts.some(p => p.id === product.id)} onToggleSelect={() => toggleProductSelection(product)} data-id="element-307" />) : <div className="col-span-2 py-8 text-center text-gray-500" data-id="element-308">
                  No products found matching your criteria
                </div> : filteredSKUs.length > 0 ? filteredSKUs.map(sku => <SKUCard key={sku.id} sku={sku} isSelected={selectedSKUs.some(s => s.id === sku.id)} onToggleSelect={() => toggleSKUSelection(sku)} data-id="element-309" />) : <div className="col-span-2 py-8 text-center text-gray-500" data-id="element-310">
                No variants found matching your criteria
              </div>}
          </div>
        </div>
        {/* Right side - Selected Items */}
        <div className="w-2/5 p-6 bg-gray-50 overflow-y-auto" data-id="element-311">
          <div className="flex justify-between items-center mb-4" data-id="element-312">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-313">
              Selected Items ({totalVariants})
            </h3>
            {hasSelectedItems && <button onClick={handleClearAll} className="text-sm text-red-600 hover:text-red-800" data-id="element-314">
                Clear All
              </button>}
          </div>
          <div className="mb-4" data-id="element-315">
            <div className="bg-white border border-gray-200 rounded-md p-3 mb-4" data-id="element-316">
              <div className="flex items-center text-sm" data-id="element-317">
                {collectionType === 'spu' ? <LayersIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-318" /> : collectionType === 'sku' ? <PackageIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-319" /> : <div className="flex items-center" data-id="element-320">
                    <LayersIcon className="h-4 w-4 text-blue-500 mr-1" data-id="element-321" />
                    <span className="mr-1" data-id="element-322">/</span>
                    <PackageIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-323" />
                  </div>}
                <span className="font-medium" data-id="element-324">
                  Adding to:{' '}
                  {collectionType === 'spu' ? 'SPU Based Collection' : collectionType === 'sku' ? 'SKU Specific Collection' : 'Mixed Collection'}
                </span>
              </div>
            </div>
            {hasSelectedItems ? <SelectedItemsList selectedProducts={selectedProducts} selectedSKUs={selectedSKUs} onRemoveProduct={productId => setSelectedProducts(selectedProducts.filter(p => p.id !== productId))} onRemoveSKU={skuId => setSelectedSKUs(selectedSKUs.filter(s => s.id !== skuId))} data-id="element-325" /> : <div className="text-center py-12 bg-white border border-dashed border-gray-300 rounded-md" data-id="element-326">
                <div className="flex justify-center mb-3" data-id="element-327">
                  {collectionType === 'spu' ? <LayersIcon className="h-12 w-12 text-gray-300" data-id="element-328" /> : collectionType === 'sku' ? <PackageIcon className="h-12 w-12 text-gray-300" data-id="element-329" /> : <div className="relative" data-id="element-330">
                      <LayersIcon className="h-12 w-12 text-gray-300" data-id="element-331" />
                      <PackageIcon className="h-8 w-8 text-gray-300 absolute -bottom-2 -right-2" data-id="element-332" />
                    </div>}
                </div>
                <p className="text-gray-500 mb-2" data-id="element-333">No items selected yet</p>
                <p className="text-sm text-gray-400" data-id="element-334">
                  Select{' '}
                  {collectionType === 'spu' ? 'products' : collectionType === 'sku' ? 'variants' : 'products or variants'}{' '}
                  from the left panel
                </p>
              </div>}
            {hasSelectedItems && <div className="mt-4 bg-white border border-gray-200 rounded-md p-3" data-id="element-335">
                <div className="flex justify-between items-center text-sm" data-id="element-336">
                  <span className="text-gray-700" data-id="element-337">Total items:</span>
                  <span className="font-medium" data-id="element-338">
                    {productCount > 0 && `${productCount} Products`}
                    {productCount > 0 && skuCount > 0 && ', '}
                    {skuCount > 0 && `${skuCount} Individual variants`}
                    {(productCount > 0 || skuCount > 0) && ` (${totalVariants} total variants)`}
                  </span>
                </div>
              </div>}
          </div>
        </div>
      </div>
      {/* Bottom Navigation */}
      <div className="border-t border-gray-200 p-6 flex justify-between" data-id="element-339">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-340">
          Back to Details
        </button>
        <div data-id="element-341">
          <button onClick={onSkip} className="text-gray-500 hover:text-gray-700 mr-4" data-id="element-342">
            Skip for Now
          </button>
          <button onClick={onNext} disabled={!hasSelectedItems} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${hasSelectedItems ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'}`} data-id="element-343">
            Next: Review
          </button>
        </div>
      </div>
    </div>;
};